import { cloneDeep } from 'lodash';
import withDraft from '@/utils/draftHelper';
import findResult, {
  assignIfExists,
  assignValue,
  findSelectedInCategory,
  mapItemOrderByValues,
  mapResults,
  removeItemByID,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { responseError } from '@/utils/errors';
import actions from '../constants';
import {
  datasetStateToLoad,
  mapResult,
  switchOffDatasetPreview,
  switchOnDatasetPreview,
} from './state';
import findDataset, { findAllDatasets, findDatasets } from './shared';
import { activeDictionary, splitUrlsInDictionaries } from '../dictionaries/state/state';
import { addUUIDsToDictionaries } from '../dictionaries/helper';

const closeDatasets = withDraft((draft, { category }) => {
  draft.datasets[category] = datasetStateToLoad({ datasets: draft }).datasets[category];
});

const convertToDictionary = withDraft((draft) => { draft.activeDictionary.saveStatus = states.PENDING; });

const convertToDictionaryError = withDraft((draft) => {
  assignIfExists(draft.activeDictionary, { ...activeDictionary(), saveStatus: states.ERROR });
});

const convertToDictionarySuccess = withDraft((draft, action) => {
  assignIfExists(draft.activeDictionary, { ...activeDictionary(), saveStatus: states.SUCCESS });

  if (action.deleteSource) {
    const categories = findAllDatasets(draft, { items: [{ code: action.code }] });

    categories.forEach((category) => {
      const item = findResult(draft.datasets, category, action.code);
      if (item?.attachments) {
        item.attachments.items = removeItemByID(item.attachments.items, { id: action.attachmentId });
      }
    });
  }
});

const createActiveDataset = withDraft((draft) => { draft.activeDataset.saveStatus = states.PENDING; });

const createAndConvertToDictionary = withDraft((draft) => {
  draft.activeDictionary.saveStatus = states.PENDING;
  draft.activeDictionary.saveErrors = [];
});

const enablePmnTransfers = withDraft((draft, { value }) => { draft.allowPmnTransfer = value; });

const fetchDatasets = withDraft((draft, action) => {
  const item = findDatasets(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const newDictionaryError = withDraft((draft, action) => {
  draft.activeDictionary.saveStatus = states.ERROR;
  draft.activeDictionary.saveErrors = [responseError(action)];
});

const newDictionarySuccess = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const dictionaries = addUUIDsToDictionaries(action.result.dictionaries);
  const dictionary = dictionaries.find((i) => i.name === action.newDictionaryName);
  const activeDictionaryUUID = dictionary ? dictionary.uuid : null;
  const props = {
    dictionaries,
    saveStatus: states.SUCCESS,
    saveErrors: [],
    activeDictionaryUUID,
    activeDictionaryName: action.newDictionaryName,
  };

  draft.activeDictionary.saveStatus = states.SUCCESS;
  draft.activeDictionary.saveErrors = [];

  if (item.isEditing) {
    props.copy = { dictionaries: cloneDeep(dictionaries) };
    splitUrlsInDictionaries(props);
    assignIfExists(item, props);
  } else {
    assignIfExists(item, { ...mapResult(action.result), ...props });
  }
});

const showDatasetsError = withDraft((draft, action) => {
  const item = findDatasets(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showDatasetsResults = withDraft((draft, action) => {
  const item = findDatasets(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append ? mapResults(item.results, action, mapResult) : mapResults([], action, mapResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showDeleteDatasets = withDraft((draft, action) => {
  draft.deleteDatasetOpen = true;
  draft.datasets.deletion.results = findSelectedInCategory(draft.datasets, action.category);
});

const toggleDatasetColumnOrderBy = withDraft((draft, { category, orderByColumn }) => {
  const item = findDatasets(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.orderBy = mapItemOrderByValues(item.orderBy, orderByColumn, item.singleSorting);
});

const toggleDatasetSelected = withDraft((draft, action) => {
  const item = findDataset(draft, action);

  if (!item.selected && !draft.datasets[action.category].multiSelect) {
    const selectedDatasets = findSelectedInCategory(draft.datasets, action.category);
    selectedDatasets.forEach((dataset) => assignValue(dataset, 'selected', false));
  }
  item.selected = !item.selected;
});

const toggleDatasetPreview = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  switchOffDatasetPreview(item) || switchOnDatasetPreview(item);
});

const toggleDatasetSingleSorting = withDraft((draft, { category }) => {
  const item = findDatasets(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.singleSorting = !item.singleSorting;
  item.orderBy = [item.orderBy[0]];
});

const toggleDatasetsSelected = withDraft((draft, { category, selected }) => {
  const item = findDatasets(draft, { category });
  item.results.forEach((result) => {
    assignValue(result, 'selected', selected);
  });
});

const updateDatasetsDataModelFilter = withDraft((draft, { category }) => {
  const item = findDatasets(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.currentPage = 1;
});

const updateDatasetsFilter = withDraft((draft, { category, value }) => {
  const item = findDatasets(draft, { category });
  item.filter = value;
});

const updateDatasetsFlags = withDraft((draft, {
  category,
  privateFlag,
  internalFlag,
  publicFlag,
}) => {
  const item = findDatasets(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.privateFlag = privateFlag;
  item.internalFlag = internalFlag;
  item.publicFlag = publicFlag;
  item.currentPage = 1;
});

const toggleDatasetsPresentation = withDraft((draft, { category, display }) => {
  const item = findDatasets(draft, { category });
  item.display = display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

export default {
  [actions.CLOSE_DATASETS]: closeDatasets,
  [actions.CONVERT_TO_DICTIONARY]: convertToDictionary,
  [actions.CONVERT_TO_DICTIONARY_ERROR]: convertToDictionaryError,
  [actions.CONVERT_TO_DICTIONARY_SUCCESS]: convertToDictionarySuccess,
  [actions.CREATE_ACTIVE_DATASET]: createActiveDataset,
  [actions.CREATE_AND_CONVERT_TO_DICTIONARY]: createAndConvertToDictionary,
  [actions.ENABLE_PMN_TRANSFERS]: enablePmnTransfers,
  [actions.FETCH_DATASETS]: fetchDatasets,
  [actions.NEW_DICTIONARY_ERROR]: newDictionaryError,
  [actions.NEW_DICTIONARY_SUCCESS]: newDictionarySuccess,
  [actions.REFRESH_DATASETS]: fetchDatasets,
  [actions.SHOW_DATASETS_ERROR]: showDatasetsError,
  [actions.SHOW_DATASETS_RESULTS]: showDatasetsResults,
  [actions.SHOW_DELETE_DATASETS]: showDeleteDatasets,
  [actions.TOGGLE_DATASET_COLUMN_ORDER_BY]: toggleDatasetColumnOrderBy,
  [actions.TOGGLE_DATASET_SELECTED]: toggleDatasetSelected,
  [actions.TOGGLE_DATASET_PREVIEW]: toggleDatasetPreview,
  [actions.TOGGLE_DATASET_SINGLE_SORTING]: toggleDatasetSingleSorting,
  [actions.TOGGLE_DATASETS_SELECTED]: toggleDatasetsSelected,
  [actions.UPDATE_DATASETS_DATA_MODEL_FILTER]: updateDatasetsDataModelFilter,
  [actions.UPDATE_DATASETS_FILTER]: updateDatasetsFilter,
  [actions.UPDATE_DATASETS_FLAGS]: updateDatasetsFlags,
  [actions.TOGGLE_DATASETS_PRESENTATION]: toggleDatasetsPresentation,
};
